import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import {
  ArrowLeft,
  ArrowRight,
  Handshake,
  Wallet,
  Users,
  TrendingUp,
  Sparkles,
  Share2,
  Link2,
  BadgeCheck,
  ShieldCheck,
  Clock,
  Quote,
  Star,
} from "lucide-react";

/**
 * Public "Partner worden" (Become a partner) page.
 *
 * Explains the JobGrid affiliate programme — what it is, why it exists, how
 * it works, the commercial terms (20% / 30-day cookie / €25 SEPA payout), and
 * how to sign up. Sign-up is *only* possible from inside a logged-in JobGrid
 * account (Profile → Affiliate tab). We deep-link authenticated visitors
 * straight to that tab via `?view=profile&tab=affiliate`.
 */
export default function Partner() {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);

  // Fetch approved *partner*-category testimonials. Falls back to an
  // "become the first ambassador" invite card when none exist yet — we
  // deliberately don't invent fake affiliate quotes.
  useEffect(() => {
    let alive = true;
    api.get("/testimonials/approved?category=partner")
      .then((r) => { if (alive) setTestimonials(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setTestimonials([]); });
    return () => { alive = false; };
  }, []);

  const goSignup = () => {
    if (user) {
      navigate("/dashboard?view=profile&tab=affiliate");
    } else {
      const redirectUrl =
        window.location.origin + "/dashboard?view=profile&tab=affiliate";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
        redirectUrl
      )}`;
    }
  };

  const benefits = [
    {
      Icon: Wallet,
      T: t("partnerBenefit1T"),
      D: t("partnerBenefit1D"),
    },
    {
      Icon: Clock,
      T: t("partnerBenefit2T"),
      D: t("partnerBenefit2D"),
    },
    {
      Icon: ShieldCheck,
      T: t("partnerBenefit3T"),
      D: t("partnerBenefit3D"),
    },
    {
      Icon: TrendingUp,
      T: t("partnerBenefit4T"),
      D: t("partnerBenefit4D"),
    },
  ];

  const steps = [
    { Icon: BadgeCheck, T: t("partnerStep1T"), D: t("partnerStep1D") },
    { Icon: Share2, T: t("partnerStep2T"), D: t("partnerStep2D") },
    { Icon: TrendingUp, T: t("partnerStep3T"), D: t("partnerStep3D") },
    { Icon: Wallet, T: t("partnerStep4T"), D: t("partnerStep4D") },
  ];

  const audience = [
    t("partnerForWho1"),
    t("partnerForWho2"),
    t("partnerForWho3"),
    t("partnerForWho4"),
    t("partnerForWho5"),
  ];

  return (
    <div className="ed-page min-h-screen" data-testid="partner-page">
      {/* Sticky glass navbar — matches Login header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/70 border-b border-black/5 px-6 sm:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="partner-logo">
          <Logo size="md" showTagline={false} />
        </Link>
        <nav className="flex items-center gap-3 sm:gap-6">
          <Link
            to="/pricing"
            data-testid="partner-nav-pricing"
            className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline"
          >
            {t("landNavPricing")}
          </Link>
          <Link
            to="/contact"
            data-testid="partner-nav-contact"
            className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline"
          >
            {t("footerContact")}
          </Link>
          <LanguageToggle testId="partner-language-toggle" />
          {user && (
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="partner-back-dashboard"
              className="ed-btn ed-btn-outline"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />{" "}
              <span className="hidden sm:inline">{t("priceBack")}</span>
            </button>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-14 sm:pt-24 pb-12 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 land-fade">
          <p className="ed-eyebrow" data-testid="partner-eyebrow">
            {t("partnerEyebrow")}
          </p>
          <h1 className="ed-h1 mt-6" data-testid="partner-h1">
            {t("partnerH1a")}{" "}
            <span className="serif-italic">{t("partnerH1accent")}</span>
            <br />
            {t("partnerH1b")}
          </h1>
          <div className="ed-rule w-24 mt-8" />
          <p className="mt-8 max-w-xl text-neutral-700 leading-relaxed">
            {t("partnerHeroSub")}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={goSignup}
              data-testid="partner-hero-cta"
              className="inline-flex items-center gap-2 bg-[#24211D] text-[#F9F8F6] px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#3D3934] transition-colors"
            >
              {user ? t("partnerCtaOpenTab") : t("partnerCtaLoginToJoin")}{" "}
              <ArrowRight size={13} strokeWidth={1.5} />
            </button>
            <Link
              to="/pricing"
              data-testid="partner-hero-secondary"
              className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              {t("partnerCtaSeePricing")} <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
          <p className="mt-6 text-xs text-neutral-500 font-mono max-w-md leading-relaxed">
            {t("partnerHeroFootnote")}
          </p>
        </div>

        {/* Hero side card — headline commission numbers */}
        <div
          className="lg:col-span-5 relative land-fade"
          style={{ animationDelay: "0.15s" }}
        >
          <div
            className="bg-gradient-to-br from-[#1A1A1A] via-[#2A2724] to-[#3D3934] text-[#F9F8F6] p-8 sm:p-10 relative overflow-hidden shadow-xl"
            data-testid="partner-hero-card"
          >
            <div className="absolute -top-8 -right-8 opacity-10">
              <Handshake size={160} strokeWidth={1.2} />
            </div>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-amber-300 relative z-10">
              {t("partnerCommissionEyebrow")}
            </p>
            <p
              className="font-serif text-6xl mt-3 relative z-10"
              data-testid="partner-commission-headline"
            >
              15%
            </p>
            <p className="text-sm text-neutral-300 mt-2 relative z-10 leading-relaxed">
              {t("partnerCommissionSub")}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 relative z-10">
              <div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-400">
                  {t("partnerFactCookie")}
                </p>
                <p className="font-serif text-2xl mt-1">30 {t("partnerDays")}</p>
              </div>
              <div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-400">
                  {t("partnerFactPayoutFrom")}
                </p>
                <p className="font-serif text-2xl mt-1">€25</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat is het? */}
      <section className="border-t border-black/5 bg-[#F0EFEA]/40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
          <p className="ed-eyebrow">{t("partnerWhatEyebrow")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 max-w-3xl leading-tight">
            {t("partnerWhatTitle")}
          </h2>
          <p className="mt-6 max-w-3xl text-neutral-700 leading-relaxed">
            {t("partnerWhatBody1")}
          </p>
          <p className="mt-4 max-w-3xl text-neutral-700 leading-relaxed">
            {t("partnerWhatBody2")}
          </p>
        </div>
      </section>

      {/* Voordelen grid */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
        <p className="ed-eyebrow">{t("partnerBenefitsEyebrow")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-4 max-w-2xl leading-tight">
          {t("partnerBenefitsTitle")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8 sm:mt-12">
          {benefits.map(({ Icon, T, D }, i) => (
            <div
              key={i}
              data-testid={`partner-benefit-${i}`}
              className="bg-white border border-black/10 p-6 sm:p-8 land-fade"
              style={{ animationDelay: `${0.05 + i * 0.05}s` }}
            >
              <Icon size={20} strokeWidth={1.5} />
              <p className="font-serif text-xl sm:text-2xl mt-4 leading-tight">
                {T}
              </p>
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{D}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hoe werkt het? */}
      <section className="border-t border-black/5 bg-[#24211D] text-[#F9F8F6]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
          <p className="ed-eyebrow text-amber-200/80">
            {t("partnerHowEyebrow")}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 max-w-2xl leading-tight">
            {t("partnerHowTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8 sm:mt-12">
            {steps.map(({ Icon, T, D }, i) => (
              <div
                key={i}
                data-testid={`partner-step-${i}`}
                className="border border-white/10 p-6 sm:p-8 bg-white/5 land-fade"
                style={{ animationDelay: `${0.05 + i * 0.05}s` }}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-amber-200/80">
                    0{i + 1}
                  </span>
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <p className="font-serif text-xl mt-4 leading-tight">{T}</p>
                <p className="text-sm text-neutral-300 mt-3 leading-relaxed">
                  {D}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner voices — real approved testimonials from category=partner.
          When none exist yet, we invite visitors to become the first
          ambassador rather than fabricate quotes. */}
      <section className="border-t border-black/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
          <p className="ed-eyebrow">{t("partnerVoicesEyebrow")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 max-w-2xl leading-tight">
            {testimonials.length > 0 ? t("partnerVoicesTitle") : t("partnerVoicesEmptyTitle")}
          </h2>

          {testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8 sm:mt-12">
              {testimonials.map((q, i) => (
                <figure
                  key={q.id || i}
                  data-testid={`partner-voice-${i}`}
                  className="bg-white border border-black/10 p-6 sm:p-7 flex flex-col land-fade"
                  style={{ animationDelay: `${0.05 + i * 0.05}s` }}
                >
                  <div className="flex items-center gap-1 text-amber-500" aria-hidden="true">
                    {[...Array(Math.max(1, Math.min(5, q.rating || 5)))].map((_, s) => (
                      <Star key={s} size={12} strokeWidth={0} fill="currentColor" />
                    ))}
                  </div>
                  <Quote size={16} strokeWidth={1.25} className="text-amber-600 mt-3" />
                  <blockquote className="font-serif italic text-base sm:text-lg text-neutral-800 leading-relaxed mt-2 flex-1">
                    “{q.body}”
                  </blockquote>
                  <figcaption className="mt-5 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-neutral-500">
                    — {[q.alias, q.city, q.role].filter(Boolean).join(" · ") || "Partner"}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div
              data-testid="partner-voices-empty"
              className="mt-8 sm:mt-12 bg-gradient-to-br from-neutral-900 to-neutral-800 text-[#F9F8F6] p-8 sm:p-12 relative overflow-hidden max-w-3xl"
            >
              <div className="absolute -top-6 -right-6 opacity-10">
                <Quote size={120} strokeWidth={1.2} />
              </div>
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-amber-300 relative z-10">
                {t("partnerVoicesEmptyEyebrow")}
              </p>
              <p className="font-serif text-xl sm:text-2xl mt-3 leading-tight max-w-xl relative z-10">
                {t("partnerVoicesEmptyBody")}
              </p>
              <button
                onClick={goSignup}
                data-testid="partner-voices-empty-cta"
                className="mt-6 inline-flex items-center gap-2 bg-[#F9F8F6] text-[#1A1A1A] px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors relative z-10"
              >
                {user ? t("partnerCtaOpenTab") : t("partnerCtaLoginToJoin")}
                <ArrowRight size={13} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Voor wie? */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-14 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="ed-eyebrow">{t("partnerForWhoEyebrow")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">
            {t("partnerForWhoTitle")}
          </h2>
          <p className="mt-6 text-neutral-700 leading-relaxed">
            {t("partnerForWhoSub")}
          </p>
        </div>
        <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {audience.map((line, i) => (
            <li
              key={i}
              data-testid={`partner-audience-${i}`}
              className="flex items-start gap-3 border border-black/10 bg-white px-4 py-4"
            >
              <Users size={15} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span className="text-sm text-neutral-800 leading-relaxed">
                {line}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Aanmelden CTA */}
      <section className="border-t border-black/5 bg-[#F0EFEA]/60">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-14 sm:py-24 text-center">
          <p className="ed-eyebrow">{t("partnerSignupEyebrow")}</p>
          <h2 className="font-serif text-3xl sm:text-5xl mt-4 leading-tight">
            {t("partnerSignupTitle")}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-neutral-700 leading-relaxed">
            {t("partnerSignupBody")}
          </p>

          <div className="mt-10 inline-flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={goSignup}
              data-testid="partner-signup-cta"
              className="inline-flex items-center gap-2 bg-[#24211D] text-[#F9F8F6] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#3D3934] transition-colors"
            >
              {user ? t("partnerCtaOpenTab") : t("partnerCtaLoginToJoin")}{" "}
              <ArrowRight size={13} strokeWidth={1.5} />
            </button>
            {!user && (
              <Link
                to="/"
                data-testid="partner-signup-secondary"
                className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                {t("partnerCtaNoAccountYet")} <ArrowRight size={12} strokeWidth={1.5} />
              </Link>
            )}
          </div>

          <div className="mt-10 max-w-xl mx-auto border border-black/10 bg-white p-5 text-left" data-testid="partner-signup-note">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-2">
              <Link2 size={11} strokeWidth={1.75} />
              {t("partnerHowToJoinLabel")}
            </p>
            <ol className="mt-3 space-y-2 text-sm text-neutral-700 leading-relaxed list-decimal list-inside marker:font-mono marker:text-neutral-400">
              <li>{t("partnerJoin1")}</li>
              <li>{t("partnerJoin2")}</li>
              <li>{t("partnerJoin3")}</li>
              <li>{t("partnerJoin4")}</li>
            </ol>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
        <p className="ed-eyebrow">{t("partnerFaqEyebrow")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">
          {t("partnerFaqTitle")}
        </h2>
        <div className="mt-8 divide-y divide-black/10 border-t border-b border-black/10">
          {[1, 2, 3, 4, 5].map((n) => (
            <details
              key={n}
              className="group py-5"
              data-testid={`partner-faq-${n}`}
            >
              <summary className="cursor-pointer font-serif text-lg sm:text-xl leading-tight flex items-start justify-between gap-4">
                <span>{t(`partnerFaq${n}Q`)}</span>
                <Sparkles
                  size={14}
                  strokeWidth={1.5}
                  className="mt-1 shrink-0 opacity-40 group-open:opacity-100 transition-opacity"
                />
              </summary>
              <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                {t(`partnerFaq${n}A`)}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/5 px-6 sm:px-10 py-10 sm:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <Logo size="md" showTagline={false} />
            <p className="text-sm text-neutral-500 mt-2 max-w-xs leading-relaxed">
              {t("landFooterTag")}
            </p>
          </div>
          <div>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mb-3">
              {t("footerCompany")}
            </p>
            <p className="font-serif text-base">CHEPL bv</p>
            <p className="text-sm text-neutral-600 mt-1">
              <a
                href="mailto:info@jobgrid.nl"
                className="hover:underline underline-offset-2"
              >
                info@jobgrid.nl
              </a>
            </p>
            <p className="font-mono text-[0.65rem] text-neutral-500 mt-2">
              KvK 42087917 · BTW NL869653817B01
            </p>
          </div>
          <nav className="flex flex-col sm:items-end gap-3">
            <Link
              to="/pricing"
              data-testid="partner-footer-pricing"
              className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              {t("landFooterPricing")}
            </Link>
            <Link
              to="/contact"
              data-testid="partner-footer-contact"
              className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              {t("footerContact")}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
